import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  VStack,
  HStack,
  Flex,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  IconButton,
  useToast,
  Select,
  Wrap,
  WrapItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Checkbox,
  useDisclosure,
  Spinner,
} from "@chakra-ui/react";
import { DownloadIcon, CopyIcon, StarIcon } from "@chakra-ui/icons";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import SendEmail from "../../components/SendEmail";

const itemsPerPage = 10;

const moodOptions = [
  { value: "Angry", label: "화남" },
  { value: "Busy & Frantic", label: "바쁘고 정신없음" },
  { value: "Changing Tempo", label: "변하는 템포" },
  { value: "Chasing", label: "쫓는 느낌" },
  { value: "Dark", label: "어두움" },
  { value: "Dreamy", label: "몽환적임" },
  { value: "Eccentric", label: "엉뚱함" },
  { value: "Elegant", label: "우아함" },
  { value: "Epic", label: "웅장함" },
  { value: "Euphoric", label: "황홀함" },
  { value: "Fear", label: "두려움" },
  { value: "Floating", label: "둥둥 떠다님" },
  { value: "Funny", label: "유쾌함" },
  { value: "Glamorous", label: "매혹적임" },
  { value: "Happy", label: "행복함" },
  { value: "Heavy & Ponderous", label: "무겁고 육중함" },
  { value: "Hopeful", label: "희망적임" },
  { value: "Laid Back", label: "느긋함" },
  { value: "Marching", label: "행진하는 느낌" },
  { value: "Mysterious", label: "신비로움" },
  { value: "Peaceful", label: "평화로움" },
  { value: "Quirky", label: "기이함" },
  { value: "Relaxing", label: "편안함" },
  { value: "Restless", label: "불안정함" },
  { value: "Romantic", label: "로맨틱함" },
  { value: "Running", label: "달림" },
  { value: "Sad", label: "슬픔" },
  { value: "Scary", label: "무서움" },
  { value: "Sentimental", label: "감상적임" },
  { value: "Sexy", label: "섹시함" },
  { value: "Smooth", label: "부드러움" },
  { value: "Sneaking", label: "살금살금 움직임" },
  { value: "Suspense", label: "서스펜스" },
  { value: "Weird", label: "이상함" },
  { value: "All moods", label: "전체 무드" },
];

// mood 영어-한글 매핑 객체 생성
const moodLabelMap = moodOptions.reduce((acc, cur) => {
  acc[cur.value] = cur.label;
  return acc;
}, {});

// 장르 옵션 고정 배열
const genreOptions = [
  "트로트",
  "Ballad (국내)",
  "Blues (국내)",
  "Dance (국내)",
  "Folk (국내)",
  "R&B / Soul (국내)",
  "Rap / Hip-hop (국내)",
  "Rock (국내)",
  "Electronica (국내)",
  "CCM (국내)",
  "Jazz (국내)",
  "OST (국내)",
  "Blues (국외)",
  "Country (국외)",
  "Electronica (국외)",
  "Folk (국외)",
  "Pop (국외)",
  "EDM (국외)",
  "R&B / Soul (국외)",
  "Rap / Hip-hop (국외)",
  "Rock (국외)",
  "CCM (국외)",
  "Jazz (국외)",
  "OST (국외)",
  "한국전통음악",
  "어린이음악",
  "종교음악",
  "Classic",
  "New Age",
  "뮤직테라피",
  "기타",
  "월드뮤직",
  "Electronica (J-Pop)",
  "Pop (J-Pop)",
  "R&B / Soul (J-Pop)",
  "Rap / Hip-hop (J-Pop)",
  "Rock (J-Pop)",
];

const AlbumList = () => {
  const { user } = useAuth();
  const [ownedAlbums, setOwnedAlbums] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [totalOwnedAlbums, setTotalOwnedAlbums] = useState(0);
  const [totalTracks, setTotalTracks] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [trackCurrentPage, setTrackCurrentPage] = useState(1);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [moods, setMoods] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState("");
  const [selectedMood, setSelectedMood] = useState("");
  const [hasContract, setHasContract] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    channel: "",
    agree: false,
  });
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const isFormValid =
    form.name && form.email && form.phone && form.channel && form.agree;
  const [isOwnershipModalOpen, setOwnershipModalOpen] = useState(false);
  const [selectedOwnershipId, setSelectedOwnershipId] = useState(null);
  const [isOwnershipLoading, setOwnershipLoading] = useState(false);
  const [userLabels, setUserLabels] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [tempPage, setTempPage] = useState(currentPage.toString());
  const [trackTempPage, setTrackTempPage] = useState(
    trackCurrentPage.toString()
  );

  // fetchTracks와 fetchOwnedAlbums를 useCallback으로 선언
  const fetchTracks = useCallback(async () => {
    try {
      setIsDataLoading(true);
      const { data, error } = await supabase.rpc(
        "fetch_rhoonart_album_tracks",
        {
          genre_keyword: selectedGenre,
          mood_keyword: selectedMood,
          page_limit: 10,
          page_offset: (trackCurrentPage - 1) * 10,
        }
      );

      if (error) {
        console.error("Error fetching tracks:", error);
        setTracks([]);
        setTotalTracks(0);
        return;
      }

      if (!data || data.length === 0) {
        setTracks([]);
        setTotalTracks(0);
        return;
      }

      const totalCount = data[0]?.total_count || 0;
      setTotalTracks(totalCount);

      const processedTracks = data.map((item) => {
        let youtubeId = "";
        if (item.youtube_url) {
          const match = item.youtube_url.match(
            /(?:v=|youtu.be\/|embed\/)([\w-]{11})/
          );
          youtubeId = match ? match[1] : "";
        }
        return {
          track_id: item.track_id,
          ownership_id: item.ownership_id,
          albumId: item.album_id,
          trackCode: item.track_code,
          youtubeId,
          youtube_url: item.youtube_url,
          track_name_kr: item.track_name,
          album: {
            cover: item.album_cover,
            name_album_kr: item.album_name,
            musicians: { name_musician: item.musician_name },
          },
          isrc: item.isrc,
          genre_depth4: item.genre || "-",
          mood: Array.isArray(item.mood) ? item.mood : item.mood || "-",
          mp3_ar: item.mp3,
          wav_ar: item.wav,
          registered_at: item.registered_at,
          published_at: item.published_at,
        };
      });
      setTracks(processedTracks);
    } catch (error) {
      console.error("Error in fetchTracks:", error);
      setTracks([]);
      setTotalTracks(0);
    } finally {
      setIsDataLoading(false);
    }
  }, [selectedGenre, selectedMood, trackCurrentPage]);

  const fetchOwnedAlbums = useCallback(async () => {
    if (!user) return;
    try {
      setIsDataLoading(true);
      const { data, error } = await supabase.rpc("fetch_user_album_tracks", {
        target_email: user.email,
        search_keyword: search,
        page_limit: 10,
        page_offset: (currentPage - 1) * 10,
      });

      if (error) throw error;

      if (!data || data.length === 0) {
        setOwnedAlbums([]);
        setTotalOwnedAlbums(0);
        return;
      }

      const totalCount = data[0]?.total_count || 0;
      setTotalOwnedAlbums(totalCount);

      const processedData = data.map((item) => {
        let youtubeId = "";
        if (item.youtube_url) {
          const match = item.youtube_url.match(
            /(?:v=|youtu.be\/|embed\/)([\w-]{11})/
          );
          youtubeId = match ? match[1] : "";
        }
        return {
          id: item.track_code,
          albumId: item.album_id,
          trackCode: item.track_code,
          youtubeId,
          youtubeUrl: item.youtube_url,
          trackName: item.track_name,
          albumName: item.album_name,
          albumCover: item.album_cover,
          artist: item.musician_name,
          isrc: item.isrc,
          genre: item.genre || "-",
          mood: Array.isArray(item.mood)
            ? item.mood.join(", ")
            : item.mood || "-",
          mp3: item.mp3,
          wav: item.wav,
          registered_at: item.registered_at,
          published_at: item.published_at,
        };
      });
      setOwnedAlbums(processedData);
    } catch (error) {
      console.error("Error fetching owned albums:", error);
      setOwnedAlbums([]);
      setTotalOwnedAlbums(0);
    } finally {
      setIsDataLoading(false);
    }
  }, [user, search, currentPage]);

  // useEffect에서 fetchTracks, fetchOwnedAlbums 호출
  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  useEffect(() => {
    fetchOwnedAlbums();
  }, [fetchOwnedAlbums]);

  useEffect(() => {
    const checkContract = async () => {
      if (!user?.email) {
        setHasContract(false);
        setIsInitialLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from("shorts_contract")
          .select("id, commission")
          .eq("contractor", user.email)
          .maybeSingle();
        if (error) {
          setHasContract(false);
          return;
        }
        setHasContract(!!data);
      } finally {
        setIsInitialLoading(false);
      }
    };
    checkContract();
  }, [user]);

  // 사용자 레이블 목록 불러오기
  useEffect(() => {
    if (!user?.email) return;
    const fetchLabels = async () => {
      const { data } = await supabase
        .from("label")
        .select("id")
        .eq("user", user.email);
      setUserLabels(data || []);
    };
    fetchLabels();
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    setIsLoadingModal(true);
    try {
      await SendEmail({
        email: "contact@plpl.kr",
        ccEmails: user?.email ? [user.email] : [],
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "jaeeui.kwon@rhoonart.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "blair.park@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "kirby.lee@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      toast({
        title: "상담 요청이 접수되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setForm({ name: "", email: "", phone: "", channel: "", agree: false });
      onClose();
    } catch (e) {
      toast({
        title: "상담 요청에 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingModal(false);
    }
  };

  const handleCopy = (isrc) => {
    navigator.clipboard.writeText(isrc);
    toast({
      title: "ISRC가 복사되었습니다.",
      status: "success",
      duration: 1200,
    });
  };

  // 페이지네이션 정보 계산
  const totalPages = Math.ceil(totalOwnedAlbums / itemsPerPage);
  const trackTotalPages = Math.ceil(totalTracks / itemsPerPage);

  const handleOwnershipSelect = (ownershipId, trackId) => {
    setSelectedOwnershipId(ownershipId);
    setOwnershipModalOpen(true);
  };

  const handleOwnershipConfirm = async () => {
    if (!selectedOwnershipId) return;
    setOwnershipLoading(true);
    try {
      // 소유권 이전과 트랙 수수료율(본인 계약 기준)은 서버 RPC 에서 함께 처리한다.
      // (RLS 로 타인 소유 ownership / track 을 직접 수정할 수 없음)
      const { error } = await supabase.rpc("claim_catalog_ownership", {
        p_ownership_id: selectedOwnershipId,
        // userLabels가 있을 경우에만 ownership_label 지정
        p_label_id: userLabels?.[0]?.id ?? null,
        p_target_email: user.email,
      });

      if (!error) {
        toast({
          title: "소유권이 성공적으로 변경되었습니다.",
          status: "success",
        });
        setOwnershipModalOpen(false);
        fetchTracks();
        fetchOwnedAlbums();
      } else {
        console.error("Ownership update error:", error);
        toast({ title: "오류가 발생했습니다.", status: "error" });
      }
    } catch (error) {
      console.error("Error in handleOwnershipConfirm:", error);
      toast({ title: "오류가 발생했습니다.", status: "error" });
    } finally {
      setOwnershipLoading(false);
    }
  };

  // 로딩 상태 체크 함수 추가
  const isLoading = isInitialLoading || (hasContract === true && isDataLoading);

  // 페이지 입력 핸들러
  const handlePageInput = (e) => setTempPage(e.target.value);
  const handlePageMove = () => {
    if (/^\d+$/.test(tempPage)) {
      const page = parseInt(tempPage);
      if (page >= 1 && page <= totalPages) setCurrentPage(page);
      else setTempPage(currentPage.toString());
    } else setTempPage(currentPage.toString());
  };
  useEffect(() => {
    setTempPage(currentPage.toString());
  }, [currentPage]);
  // 트랙용
  const handleTrackPageInput = (e) => setTrackTempPage(e.target.value);
  const handleTrackPageMove = () => {
    if (/^\d+$/.test(trackTempPage)) {
      const page = parseInt(trackTempPage);
      if (page >= 1 && page <= trackTotalPages) setTrackCurrentPage(page);
      else setTrackTempPage(trackCurrentPage.toString());
    } else setTrackTempPage(trackCurrentPage.toString());
  };
  useEffect(() => {
    setTrackTempPage(trackCurrentPage.toString());
  }, [trackCurrentPage]);

  if (hasContract === null) {
    return (
      <Flex minH="60vh" align="center" justify="center" direction="column">
        <Spinner size="xl" color="teal.500" />
      </Flex>
    );
  }
  if (hasContract === false) {
    return (
      <Flex minH="60vh" direction="column" align="center" justify="center">
        <Text fontSize="md" color="gray.700" mb={2}>
          신규 회원은 레이블리와 계약 체결 후 이용이 가능합니다.
        </Text>
        <Button colorScheme="yellow" size="lg" mt={2} onClick={onOpen}>
          계약 상담 신청하기
        </Button>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent maxW="lg">
            <ModalHeader>레이블리 이용 계약 문의하기</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4} fontSize="md">
                신규 회원은 개별 계약 후 이용이 가능합니다. 이용 계약 상담을
                신청해주시면, 기재해주신 연락처로 연락드리겠습니다.
                <br />
                (이메일만 기재할 경우, 스팸으로 분류되는 경우가 많습니다. 이용
                계약 문의주시면, 연락처로 연락드립니다.)
              </Text>
              <FormControl mb={3} isRequired>
                <FormLabel>성함 (필수)</FormLabel>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="성함"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>이메일 (필수)</FormLabel>
                <Input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="이메일"
                  type="email"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>연락처 (필수)</FormLabel>
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="연락처"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>운영중인 유튜브 채널 (필수)</FormLabel>
                <Input
                  name="channel"
                  value={form.channel}
                  onChange={handleChange}
                  placeholder="유튜브 채널"
                />
              </FormControl>
              <Checkbox
                name="agree"
                isChecked={form.agree}
                onChange={handleChange}
                mb={2}
              >
                (필수) 레이블리 이용 상담을 위한 개인정보 제공에 동의합니다.
              </Checkbox>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={isLoadingModal}
                isDisabled={!isFormValid}
                w="100%"
                size="lg"
              >
                상담 요청하기
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="container.xl" py={8}>
        {/* 상단 안내 배너 */}
        <Box
          bgGradient="linear(to-r, #8ee9ce, #4ecdc4)"
          borderRadius="xl"
          p={10}
          mb={6}
          cursor="pointer"
          onClick={() =>
            window.open(
              "https://rhoonart.notion.site/for-2ee6f04106cc40b69fca060b52d63615",
              "_blank"
            )
          }
        >
          <Flex align="center" justify="space-between" flexWrap="wrap">
            <Box>
              <Heading size="md" color="black" mb={2}>
                레이블리 for 쇼츠가 처음이신가요?
                <br />
                음원 사용 가이드를 꼭 확인해주세요!
              </Heading>
              <Text color="blackAlpha.800">
                여기를 클릭하면, 가이드 화면으로 이동해요.
              </Text>
            </Box>
          </Flex>
        </Box>

        {/* 보유중인 음반 안내 및 검색 */}
        <Flex align="center" mb={4} gap={4}>
          <Tag
            colorScheme="teal"
            borderRadius="full"
            px={4}
            py={2}
            fontSize="md"
          >
            보유중인 음반
          </Tag>
          <Text color="gray.600">
            보유하고 있는 음반이며, 해당 음반에서 발생되는 저작인접권료 수익을
            정산받아요.
          </Text>
          <Input
            placeholder="보유중인 음반 검색"
            maxW="250px"
            ml="auto"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            bg="white"
          />
          <Button
            colorScheme="teal"
            ml={2}
            onClick={() => {
              setSearch(searchInput);
              setCurrentPage(1);
            }}
          >
            검색
          </Button>
        </Flex>

        {/* 테이블 */}
        <Box bg="white" borderRadius="xl" boxShadow="md" p={0}>
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>유튜브 링크 및 재생</Th>
                <Th>음반</Th>
                <Th>ISRC</Th>
                <Th>장르</Th>
                <Th>분위기</Th>
                <Th>다운로드</Th>
                <Th>음반 선택</Th>
              </Tr>
            </Thead>
            <Tbody>
              {ownedAlbums.map((track, index) => (
                <Tr key={track.id || index}>
                  <Td>
                    {playingIndex === index && track.youtubeId ? (
                      <Box w="180px" h="100px">
                        <iframe
                          width="180"
                          height="100"
                          src={`https://www.youtube.com/embed/${track.youtubeId}?autoplay=1`}
                          title={track.trackName}
                          frameBorder="0"
                          allow="autoplay; encrypted-media"
                          allowFullScreen
                        />
                      </Box>
                    ) : (
                      <Box
                        position="relative"
                        w="180px"
                        h="100px"
                        borderRadius="lg"
                        overflow="hidden"
                        bg="gray.200"
                      >
                        <Image
                          src={
                            track.youtubeId
                              ? `https://img.youtube.com/vi/${track.youtubeId}/mqdefault.jpg`
                              : "/images/youtube-logo.png"
                          }
                          alt={track.trackName}
                          w="100%"
                          h="100%"
                          objectFit="cover"
                        />
                        <Box
                          position="absolute"
                          top="0"
                          left="0"
                          w="100%"
                          h="100%"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {track.youtubeId && (
                            <Button
                              onClick={() =>
                                window.open(
                                  `https://www.youtube.com/watch?v=${track.youtubeId}`,
                                  "_blank"
                                )
                              }
                              leftIcon={
                                <Image
                                  src="/images/youtube-play.png"
                                  w="40px"
                                  h="40px"
                                  alt="재생"
                                />
                              }
                              size="sm"
                              bg="transparent"
                              colorScheme="transparent"
                            ></Button>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Td>
                  <Td>
                    <HStack>
                      <Image
                        src={
                          track.albumCover ||
                          track.album?.cover ||
                          "/images/default-album.png"
                        }
                        alt={track.albumName}
                        boxSize="40px"
                        borderRadius="md"
                      />
                      <Box>
                        <Text fontWeight="bold">{track.trackName}</Text>
                        <Text color="gray.500" fontSize="sm">
                          {track.artist}
                        </Text>
                      </Box>
                    </HStack>
                  </Td>
                  <Td>
                    <HStack>
                      <Text>{track.isrc}</Text>
                      <IconButton
                        icon={<CopyIcon />}
                        size="xs"
                        variant="ghost"
                        aria-label="ISRC 복사"
                        onClick={() => handleCopy(track.isrc)}
                      />
                    </HStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm" fontWeight="medium">
                      {track.genre}
                    </Text>
                  </Td>
                  <Td>
                    <Wrap>
                      {typeof track.mood === "string" ? (
                        (() => {
                          const moods = track.mood
                            .replace(/[[\]"']/g, "")
                            .split(/,|\//)
                            .filter((word) => word.trim() !== "")
                            .map((m) => m.trim());
                          const filteredMoods =
                            moods.length > 1
                              ? moods.filter((m) => m !== "All moods")
                              : moods;
                          return filteredMoods.map((m, i) => (
                            <WrapItem key={i}>
                              <Box
                                fontSize="sm"
                                fontWeight="medium"
                                bg="gray.100"
                                borderRadius="md"
                                border="1px solid #CBD5E0"
                                px={2}
                                py={0.5}
                                color="gray.700"
                              >
                                {moodLabelMap[m] || m}
                              </Box>
                            </WrapItem>
                          ));
                        })()
                      ) : (
                        <Box
                          fontSize="sm"
                          fontWeight="medium"
                          bg="gray.100"
                          borderRadius="md"
                          border="1px solid #CBD5E0"
                          px={2}
                          py={0.5}
                          color="gray.700"
                        >
                          {moodLabelMap[
                            typeof track.mood === "string"
                              ? track.mood.trim()
                              : track.mood
                          ] ||
                            (typeof track.mood === "string"
                              ? track.mood.trim()
                              : track.mood)}
                        </Box>
                      )}
                    </Wrap>
                  </Td>
                  <Td>
                    <IconButton
                      icon={<DownloadIcon />}
                      aria-label="다운로드"
                      variant="outline"
                      colorScheme="teal"
                      size="lg"
                      isDisabled={!track.mp3 && !track.wav}
                      onClick={() => {
                        if (track.mp3) window.open(track.mp3, "_blank");
                        else if (track.wav) window.open(track.wav, "_blank");
                      }}
                    />
                  </Td>
                  <Td>
                    <Button
                      colorScheme="teal"
                      size="lg"
                      w="100%"
                      borderRadius="md"
                      isDisabled
                    >
                      보유중
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <Flex justify="center" mt={4} align="center">
            <Button
              onClick={() => setCurrentPage(1)}
              isDisabled={currentPage === 1}
              size="sm"
              mr={1}
            >
              처음
            </Button>
            <Button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              isDisabled={currentPage === 1}
              size="sm"
              mr={2}
            >
              이전
            </Button>
            <HStack spacing={1} mx={4}>
              <Input
                value={tempPage}
                onChange={handlePageInput}
                size="sm"
                width="50px"
                textAlign="center"
                fontSize="sm"
              />
              <Button
                size="sm"
                onClick={handlePageMove}
                colorScheme="gray"
                variant="outline"
                minW="40px"
                px={2}
                color="gray.600"
                borderColor="gray.300"
                _hover={{ bg: "gray.100", borderColor: "gray.400" }}
              >
                이동
              </Button>
              <Text>
                / {totalPages} 페이지 (전체 {totalOwnedAlbums}개)
              </Text>
            </HStack>
            <Button
              onClick={() => setCurrentPage((p) => p + 1)}
              isDisabled={currentPage >= totalPages}
              size="sm"
              ml={2}
            >
              다음
            </Button>
            <Button
              onClick={() => setCurrentPage(totalPages)}
              isDisabled={currentPage >= totalPages}
              size="sm"
              ml={1}
            >
              마지막
            </Button>
          </Flex>
        )}

        {/* 음반 리스트 섹션 */}
        <Box mt={12}>
          <Flex align="center" justify="space-between" mb={5}>
            <Tag
              colorScheme="teal"
              borderRadius="full"
              px={4}
              py={2}
              fontSize="md"
            >
              음반 리스트
            </Tag>
            <Flex gap={4}>
              <Select
                placeholder="장르 선택"
                value={selectedGenre}
                onChange={(e) => {
                  setSelectedGenre(e.target.value);
                  setTrackCurrentPage(1);
                }}
                maxW="200px"
                bg="white"
              >
                {genreOptions.map((genre) => (
                  <option key={genre} value={genre}>
                    {genre}
                  </option>
                ))}
              </Select>
              <Select
                placeholder="분위기 선택"
                value={selectedMood}
                onChange={(e) => {
                  setSelectedMood(e.target.value);
                  setTrackCurrentPage(1);
                }}
                maxW="200px"
                bg="white"
              >
                {moodOptions.map((mood) => (
                  <option key={mood.value} value={mood.value}>
                    {mood.label}
                  </option>
                ))}
              </Select>
            </Flex>
          </Flex>

          {/* 트랙 테이블 */}
          <Box bg="white" borderRadius="xl" boxShadow="md" p={0}>
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>유튜브 링크 및 재생</Th>
                  <Th>음반</Th>
                  <Th>ISRC</Th>
                  <Th>장르</Th>
                  <Th>분위기</Th>
                  <Th>음반 선택</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tracks.map((track, index) => (
                  <Tr key={track.id}>
                    <Td>
                      {playingIndex === `track_${index}` &&
                      track.youtube_url ? (
                        <Box w="180px" h="100px">
                          <iframe
                            width="180"
                            height="100"
                            src={`https://www.youtube.com/embed/${
                              track.youtube_url.match(
                                /(?:v=|youtu.be\/|embed\/)([\w-]{11})/
                              )?.[1]
                            }?autoplay=1`}
                            title={track.track_name_kr}
                            frameBorder="0"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                          />
                        </Box>
                      ) : (
                        <Box
                          position="relative"
                          w="180px"
                          h="100px"
                          borderRadius="lg"
                          overflow="hidden"
                          bg="gray.200"
                        >
                          <Image
                            src={
                              track.youtube_url
                                ? `https://img.youtube.com/vi/${
                                    track.youtube_url.match(
                                      /(?:v=|youtu.be\/|embed\/)([\w-]{11})/
                                    )?.[1]
                                  }/mqdefault.jpg`
                                : "/images/youtube-play.png"
                            }
                            alt={track.track_name_kr}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                          />
                          <Box
                            position="absolute"
                            top="0"
                            left="0"
                            w="100%"
                            h="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {track.youtube_url && (
                              <Button
                                onClick={() =>
                                  window.open(
                                    `https://www.youtube.com/watch?v=${
                                      track.youtube_url.match(
                                        /(?:v=|youtu.be\/|embed\/)([\w-]{11})/
                                      )?.[1]
                                    }`,
                                    "_blank"
                                  )
                                }
                                leftIcon={
                                  <Image
                                    src="/images/youtube-play.png"
                                    w="40px"
                                    h="40px"
                                    alt="재생"
                                  />
                                }
                                size="sm"
                                bg="transparent"
                                colorScheme="transparent"
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </Td>
                    <Td>
                      <HStack>
                        <Image
                          src={
                            track.album?.cover ||
                            track.album?.cover_url ||
                            "/images/default-album.png"
                          }
                          alt={track.album?.name_album_kr}
                          boxSize="40px"
                          borderRadius="md"
                        />
                        <Box>
                          <Text fontWeight="bold">{track.track_name_kr}</Text>
                          <Text color="gray.500" fontSize="sm">
                            {track.album?.musicians?.name_musician}
                          </Text>
                        </Box>
                      </HStack>
                    </Td>
                    <Td>
                      <HStack>
                        <Text>{track.isrc}</Text>
                        <IconButton
                          icon={<CopyIcon />}
                          size="xs"
                          variant="ghost"
                          aria-label="ISRC 복사"
                          onClick={() => handleCopy(track.isrc)}
                        />
                      </HStack>
                    </Td>
                    <Td>
                      <Text fontSize="sm" fontWeight="medium">
                        {track.genre_depth4}
                      </Text>
                    </Td>
                    <Td>
                      <Wrap>
                        {typeof track.mood === "string" ? (
                          (() => {
                            const moods = track.mood
                              .replace(/[[\]"']/g, "")
                              .split(/,|\//)
                              .filter((word) => word.trim() !== "")
                              .map((m) => m.trim());
                            const filteredMoods =
                              moods.length > 1
                                ? moods.filter((m) => m !== "All moods")
                                : moods;
                            return filteredMoods.map((m, i) => (
                              <WrapItem key={i}>
                                <Box
                                  fontSize="sm"
                                  fontWeight="medium"
                                  bg="gray.100"
                                  borderRadius="md"
                                  border="1px solid #CBD5E0"
                                  px={2}
                                  py={0.5}
                                  color="gray.700"
                                >
                                  {moodLabelMap[m] || m}
                                </Box>
                              </WrapItem>
                            ));
                          })()
                        ) : (
                          <Box
                            fontSize="sm"
                            fontWeight="medium"
                            bg="gray.100"
                            borderRadius="md"
                            border="1px solid #CBD5E0"
                            px={2}
                            py={0.5}
                            color="gray.700"
                          >
                            {moodLabelMap[
                              typeof track.mood === "string"
                                ? track.mood.trim()
                                : track.mood
                            ] ||
                              (typeof track.mood === "string"
                                ? track.mood.trim()
                                : track.mood)}
                          </Box>
                        )}
                      </Wrap>
                    </Td>
                    <Td>
                      <Button
                        rightIcon={<StarIcon />}
                        colorScheme="teal"
                        variant="outline"
                        size="lg"
                        w="100%"
                        onClick={() =>
                          handleOwnershipSelect(
                            track.ownership_id,
                            track.track_id
                          )
                        }
                      >
                        선택
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* 트랙 페이지네이션 */}
          {trackTotalPages > 1 && (
            <Flex justify="center" mt={4} align="center">
              <Button
                onClick={() => setTrackCurrentPage(1)}
                isDisabled={trackCurrentPage === 1}
                size="sm"
                mr={1}
              >
                처음
              </Button>
              <Button
                onClick={() => setTrackCurrentPage((p) => Math.max(1, p - 1))}
                isDisabled={trackCurrentPage === 1}
                size="sm"
                mr={2}
              >
                이전
              </Button>
              <HStack spacing={1} mx={4}>
                <Input
                  value={trackTempPage}
                  onChange={handleTrackPageInput}
                  size="sm"
                  width="50px"
                  textAlign="center"
                  fontSize="sm"
                />
                <Button
                  size="sm"
                  onClick={handleTrackPageMove}
                  colorScheme="gray"
                  variant="outline"
                  minW="40px"
                  px={2}
                  color="gray.600"
                  borderColor="gray.300"
                  _hover={{ bg: "gray.100", borderColor: "gray.400" }}
                >
                  이동
                </Button>
                <Text>
                  / {trackTotalPages} 페이지 (전체 {totalTracks}개)
                </Text>
              </HStack>
              <Button
                onClick={() => setTrackCurrentPage((p) => p + 1)}
                isDisabled={trackCurrentPage >= trackTotalPages}
                size="sm"
                ml={2}
              >
                다음
              </Button>
              <Button
                onClick={() => setTrackCurrentPage(trackTotalPages)}
                isDisabled={trackCurrentPage >= trackTotalPages}
                size="sm"
                ml={1}
              >
                마지막
              </Button>
            </Flex>
          )}
        </Box>
      </Container>
      <Modal
        isOpen={isOwnershipModalOpen}
        onClose={() => setOwnershipModalOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxW="lg">
          <ModalHeader>{`음반 소유권 동의`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="start" spacing={4}>
              <Text fontWeight="bold" fontSize="lg">
                음반을 선택하시겠어요?
              </Text>
              <HStack>
                <Box color="teal.400">✔️</Box>
                <Text>
                  음반을 선택하면, 해당 음반의 저작인접권료를 받을 수 있어요.
                </Text>
              </HStack>
              <HStack>
                <Box color="teal.400">✔️</Box>
                <Text>
                  해당 음반은 국내/외 음원사이트(예: 멜론, 스포티파이) 및
                  영상사이트(예: 유튜브, 틱톡, 인스타그램)에서도 확인 및 사용할
                  수 있어요.
                </Text>
              </HStack>
              <HStack>
                <Box color="teal.400">✔️</Box>
                <Text>
                  레이블리 for 쇼츠와 계약 된 국내/외 음원 및 영상 서비스에서
                  발생하는 저작인접권료 수익을 제공해요.
                </Text>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setOwnershipModalOpen(false)} mr={3}>
              취소
            </Button>
            <Button
              colorScheme="teal"
              onClick={handleOwnershipConfirm}
              isLoading={isOwnershipLoading}
            >
              동의 및 확인
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AlbumList;
